const ANGLE_BRACKET_PATTERN = /[<>]/g;

const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "radio",
  "file",
  "submit",
  "reset",
  "image",
  "hidden",
  "color",
  "range",
]);

/**
 * Removes `<` and `>` characters from a string.
 * @param {string} value
 * @returns {string}
 */
export const stripAngleBrackets = (value = "") =>
  String(value).replace(ANGLE_BRACKET_PATTERN, "");

/**
 * Returns true when the given DOM node is a free-text input or textarea.
 * Contenteditable editors (e.g. Quill) are intentionally excluded.
 * @param {EventTarget | null} target
 * @returns {boolean}
 */
export const isTextEntryElement = (target) => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return false;

  const tagName = target.tagName;
  if (tagName === "TEXTAREA") return true;
  if (tagName !== "INPUT") return false;

  const type = (target.getAttribute("type") || "text").toLowerCase();
  return !NON_TEXT_INPUT_TYPES.has(type);
};

const setNativeInputValue = (element, value) => {
  const prototype =
    element.tagName === "TEXTAREA"
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  const valueSetter = Object.getOwnPropertyDescriptor(prototype, "value")?.set;
  if (valueSetter) {
    valueSetter.call(element, value);
  } else {
    element.value = value;
  }
  element.dispatchEvent(new Event("input", { bubbles: true }));
};

const insertTextAtCursor = (element, text) => {
  const start = element.selectionStart ?? element.value.length;
  const end = element.selectionEnd ?? element.value.length;
  const nextValue = `${element.value.slice(0, start)}${text}${element.value.slice(end)}`;
  const nextCaret = start + text.length;
  setNativeInputValue(element, nextValue);
  if (typeof element.setSelectionRange === "function") {
    element.setSelectionRange(nextCaret, nextCaret);
  }
};

const handleKeyDown = (event) => {
  if (!isTextEntryElement(event.target)) return;
  if (event.key === "<" || event.key === ">") {
    event.preventDefault();
  }
};

const handleBeforeInput = (event) => {
  if (!isTextEntryElement(event.target)) return;
  if (typeof event.data === "string" && ANGLE_BRACKET_PATTERN.test(event.data)) {
    event.preventDefault();
  }
};

const handlePaste = (event) => {
  if (!isTextEntryElement(event.target)) return;

  const pastedText = event.clipboardData?.getData("text/plain") ?? "";
  if (!ANGLE_BRACKET_PATTERN.test(pastedText)) return;

  event.preventDefault();
  insertTextAtCursor(event.target, stripAngleBrackets(pastedText));
};

const handleDrop = (event) => {
  if (!isTextEntryElement(event.target)) return;

  const droppedText = event.dataTransfer?.getData("text/plain") ?? "";
  if (!ANGLE_BRACKET_PATTERN.test(droppedText)) return;

  event.preventDefault();
  insertTextAtCursor(event.target, stripAngleBrackets(droppedText));
};

/**
 * Installs a single document-level filter that blocks `<` and `>`
 * in all text inputs and textareas (typing, paste, and drop).
 * @returns {() => void} cleanup function
 */
export const enableAngleBracketInputFilter = () => {
  document.addEventListener("keydown", handleKeyDown, true);
  document.addEventListener("beforeinput", handleBeforeInput, true);
  document.addEventListener("paste", handlePaste, true);
  document.addEventListener("drop", handleDrop, true);

  return () => {
    document.removeEventListener("keydown", handleKeyDown, true);
    document.removeEventListener("beforeinput", handleBeforeInput, true);
    document.removeEventListener("paste", handlePaste, true);
    document.removeEventListener("drop", handleDrop, true);
  };
};
